import { Box, Grid, SelectChangeEvent, Theme, Typography, useTheme } from "@mui/material";
import { CreditRatingProps } from "./types";
import { useHistory } from "react-router";
import { filterType } from "../../../redux-store/types/api-types";
import { BenchmarkPerformanceRes, CreditRatingRes, GetQuantitativeAnalysisResponseBody, GetTop10SchemesRes, RatingAndMaturityRes } from "../../../redux-store/types/products";
import { Top10BondHoldings } from "./tables/bondHoldings";
import { BondsQuantativeAnalysis } from "./tables/quantativeAnalysis";
import PieChartComponent from "../directEquity/directEquityOverview/PieChartComponent";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootStateType } from "../../../redux-store/reducers";
import { NO_DATA_ERROR_MESSAGE, USER_ROLES } from "../../../utils/constant";
import { navigationData as NavigationData } from "../types";
import { getBenchmarkPerformance, getCreditRatingExposure, getQuantitativeAnalysis, top10Schemes } from "../../../redux-store/actions/product";
import { calculateOthersInPieChart } from "../../../utils/utilityFunctions";
import BondsPerformance from "./bondsPerformance";
import { HistoricalNavItem } from "../../../redux-store/types/investorProfile";



const CreditRatingExposure = (props: CreditRatingProps) => {
    const { isLoading, data, clientCode, completeData, selectedDropdownOption, handleCreditDropdown } = props;
    const [open, setOpen] = useState(false)
    const { palette } = useTheme()
    const history = useHistory()


    const pieChartLegends = data?.data.map(([currentLabel, noOfSchemes]) => {
        const labelObj = data?.labelData.find(item => item.label === currentLabel);
        return {
            label: `${currentLabel}`,
            noOfSchemes: Number(noOfSchemes),
            value: labelObj ? labelObj.value : ""
        };
    });

    const pieChartData = data?.data.map(([currentLabel, value]) => {
        const labelObj = data?.labelData.find(item => item.label === currentLabel);
        return [`${currentLabel}`, value];
    });

    return (
        <Box sx={{
            padding: '25px 30px 45px 30px',
            background: palette.common.white,
            borderRadius: '20px',
            height: '100%',
            width: '100%'
        }}>
            {(!isLoading && !completeData?.isSuccess) ? <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%"
                }}
            >
                <Typography
                    variant="body_600"
                    sx={(theme: Theme) => ({
                        fontWeight: 500,
                        lineHeight: "22.5px",
                        letterSpacing: "0.01px",
                        color: theme.palette.text.primary
                    })}
                >
                    {NO_DATA_ERROR_MESSAGE}
                </Typography>
            </Box> :
                <>
                    <PieChartComponent
                        title="Credit Rating Allocation"
                        isLoading={isLoading}
                        selectedDropdownOption={selectedDropdownOption}
                        handleDropdownValueChange={handleCreditDropdown}
                        dropdownOptions={[
                            {
                                "label": "Credit Rating Allocation",
                                "value": 1
                            },
                            {
                                "label": "Average Maturity Allocation",
                                "value": 2
                            },
                        ]}
                        pieChartData={pieChartData}
                        pieChartLabelData={data?.labelData}
                        pieChartTotalValue={data?.totalValue as filterType}
                        isSuccess={completeData?.isSuccess}
                        open={open}
                        setOpen={setOpen}

                    />
                </>
            }
        </Box>
    )
}
export default function BondsOverview(): JSX.Element {
    const history = useHistory();
    const dispatch = useDispatch();
    const { pathname: pathName, state } = history.location
    const navState = state as NavigationData
    const initialRouter = pathName.split('/').filter((item: string) => item !== '')
    const { selectedPortfolio } = useSelector((store: RootStateType) => store.selectedPortfiolio);
    const { role = '' } = useSelector((store: RootStateType) => store.auth);
    const { iwappRmId = "", investorClientId = "" } = useSelector((store: RootStateType) => store.id);
    const { investorRmId = "" } = useSelector((store: RootStateType) => store.sessionData);
    const clientCode = ![USER_ROLES.INVESTOR].includes(role) ? navState?.clientCode ?? '' : investorClientId;

    const [creditWiseLoading, setCreditWiseLoading] = useState<boolean>(false)
    const [creditWiseData, setCreditWiseData] = useState<RatingAndMaturityRes>({} as RatingAndMaturityRes)
    const [top10BondsLoader, setTop10BondsLoader] = useState<boolean>(false);
    const [top10BondsData, setTop10BondsData] = useState<GetTop10SchemesRes>({} as GetTop10SchemesRes);
    const [bondsQAData, setBondsQAData] = useState<any>([]);
    const [bondsQALoader, setBondsQALoader] = useState<boolean>(false);
    const [bondsPerformanceData, setBondsPerformanceData] = useState<BenchmarkPerformanceRes>({} as BenchmarkPerformanceRes);
    const [bondsPerformanceLoader, setBondsPerformanceLoader] = useState<boolean>(false);
    const [selectedDropdownOption, setSelectedDropdownOption] = useState<string>('1');
    const [benchMarkData, setBenchMarkData] = useState<Array<HistoricalNavItem>>([]);
    const fetchBondsRating = async (filterCategory: number) => {
        try {
            setCreditWiseLoading(true);
            const ratingPayload = {
                rmId: pathName.includes('product') ? `${iwappRmId}` : `${investorRmId}`,
                clientCode,
                portfolioId: !initialRouter.includes('product') ? selectedPortfolio?.value : "",
                assetGroup: 'DB',
                filter: filterCategory === 1 ? 'ratingName' : 'maturityYear'
            }
            const creditRatingRes = await (dispatch(getCreditRatingExposure(ratingPayload)) as unknown as Promise<CreditRatingRes>)
            setCreditWiseData({ ...creditRatingRes, result: { ...calculateOthersInPieChart(creditRatingRes?.result) } })
        }
        catch (e) {
            console.log('Error', e);
        } finally {
            setCreditWiseLoading(false);
        }
    }

    const top10Bonds = async () => {
        try {
            setTop10BondsLoader(true);
            const topSchemesPayload = {
                rmId: pathName.includes('product') ? `${iwappRmId}` : `${investorRmId}`,
                category: '',
                clientCode,
                portfolioId: !initialRouter.includes('product') ? selectedPortfolio?.value : "",
                assetGroup: 'DB',
                isTop: true,
            }
            const top10BondsRes = await (dispatch(top10Schemes(topSchemesPayload)) as unknown as Promise<GetTop10SchemesRes>)
            setTop10BondsData(top10BondsRes)
        } catch (e) {
            console.log('Error', e)
        } finally {
            setTop10BondsLoader(false);
        }
    }

    const fetchBondsQAData = async () => {
        try {
            setBondsQALoader(true)

            const payload = {
                rmId: pathName.includes('product') ? `${iwappRmId}` : `${investorRmId}`,
                category: '',
                clientCode,
                portfolioId: !initialRouter.includes('product') ? selectedPortfolio?.value : "",
                assetGroup: 'DB',
            }

            const bondsQARes = await (dispatch(getQuantitativeAnalysis(payload)) as unknown as Promise<GetQuantitativeAnalysisResponseBody>)

            if (bondsQARes?.isSuccess) {
                setBondsQAData(bondsQARes)
            } else {
                console.log(bondsQARes?.message)
            }


        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setBondsQALoader(false)
        }
    }

    const fetchBenchmarkPerformance = async () => {
        try {
            setBondsPerformanceLoader(true);
            const payload = {
                rmId: pathName.includes('product') ? `${iwappRmId}` : `${investorRmId}`,
                clientCode,
                portfolioId: !initialRouter.includes('product') ? selectedPortfolio?.value : "",
                assetGroup: 'DB',
            }
            const bondsPerformanceRes = await (dispatch(getBenchmarkPerformance(payload)) as unknown as Promise<BenchmarkPerformanceRes>)
            setBondsPerformanceData(bondsPerformanceRes);
            setBenchMarkData(bondsPerformanceRes?.result?.data1?.length > 0 ? [...bondsPerformanceRes?.result?.data1] : [])
        }
        catch (e) {
            console.log('Error', e);
        } finally {
            setBondsPerformanceLoader(false)
        }
    }


    useEffect(() => {
        const initialDataFetch = async () => {
            await Promise.allSettled([
                fetchBondsRating(1),
                top10Bonds(),
                fetchBondsQAData(),
                fetchBenchmarkPerformance()
            ])
        }
        initialDataFetch();
    }, [])

    const handleCreditDropdown = async (event: SelectChangeEvent) => {
        setSelectedDropdownOption(event.target.value)
        await fetchBondsRating(Number(event.target.value))
    }


    return (
        <Box>
            <Grid container spacing={2}>
                <Grid item lg={4} md={12} sx={{ width: '100%' }}>
                    <CreditRatingExposure
                        data={creditWiseData?.result}
                        isLoading={creditWiseLoading}
                        clientCode={clientCode ?? ""}
                        completeData={creditWiseData}
                        selectedDropdownOption={selectedDropdownOption}
                        setSelectedDropdownOption={setSelectedDropdownOption}
                        handleCreditDropdown={handleCreditDropdown}
                    />
                </Grid>

                <Grid item lg={8} md={12} sx={{ width: '100%' }}>
                    <BondsPerformance
                        isLoading={bondsPerformanceLoader}
                        getAllData={bondsPerformanceData?.result ?? []}
                        completeData={bondsPerformanceData}
                        benchmarkData={benchMarkData}
                    />
                </Grid>

                <Grid item lg={12} md={12} sm={12} sx={{ width: '100%' }}>
                    <Top10BondHoldings
                        title='Top 10 Debt-Holdings'
                        tableData={top10BondsData?.result?.topSchemes?.data}
                        isLoading={top10BondsLoader}
                        clientCode={clientCode}
                        completeData={top10BondsData}
                    // handleTableSorting={handleCategoryTableSorting}
                    // sortingObj={sortingObj}
                    // setSortingObj={setSortingObj}
                    />
                </Grid>
                {/* <Grid item lg={12} md={12} sm={12} sx={{ width: '100%' }}>
                    <Top10Bonds
                        title='Top 10 Bonds'
                        tableData={top10BondsData?.result?.topSchemes?.data}
                        isLoading={top10BondsLoader}
                        clientCode={clientCode}
                    
                    />
                </Grid> */}
                <Grid item lg={12} md={12} sm={12} sx={{ width: '100%' }}>
                    <BondsQuantativeAnalysis
                        title='Quantative Analysis'
                        tableData={bondsQAData?.result}
                        isLoading={bondsQALoader}
                        clientCode={clientCode}
                        completeData={bondsQAData}
                    // handleTableSorting={handleCategoryTableSorting}
                    // sortingObj={sortingObj}
                    // setSortingObj={setSortingObj}
                    />
                </Grid>
            </Grid>
        </Box>
    )
}

